import { useState, useEffect, useCallback } from 'react';
import { api } from '@/api/client';

interface User {
  id: string; email: string; name: string; cognito_sub: string | null;
  is_feedbacker: boolean; is_admin: boolean; default_reviewer_name: string | null;
}
interface Feedbacker { id: string; name: string; email: string }

function RoleBadge({ user }: { user: User }) {
  if (user.is_admin) return <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">管理者</span>;
  if (user.is_feedbacker) return <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">フィードバッカー</span>;
  return <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">コーチ</span>;
}

export function CoachManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [feedbackers, setFeedbackers] = useState<Feedbacker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'coach' | 'feedbacker' | 'admin'>('coach');
  const [inviteDefaultReviewer, setInviteDefaultReviewer] = useState('');
  const [inviting, setInviting] = useState(false);

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editReviewerId, setEditReviewerId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersResp, fbResp] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/feedbackers'),
      ]);
      setUsers(usersResp.data);
      setFeedbackers(fbResp.data);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'エラー');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async () => {
    if (!inviteEmail || !inviteName) return;
    setInviting(true);
    try {
      await api.post('/admin/users', {
        email: inviteEmail, name: inviteName,
        is_feedbacker: inviteRole === 'feedbacker' || inviteRole === 'admin',
        is_admin: inviteRole === 'admin',
      });
      // default_reviewer を設定（ユーザー作成後）
      if (inviteDefaultReviewer) {
        const usersResp = await api.get('/admin/users');
        const newUser = usersResp.data.find((u: User) => u.email === inviteEmail.trim().toLowerCase());
        if (newUser) {
          await api.patch(`/admin/users/${newUser.id}`, { default_reviewer_id: inviteDefaultReviewer });
        }
      }
      setShowInvite(false);
      setInviteEmail(''); setInviteName(''); setInviteRole('coach' as 'coach' | 'feedbacker' | 'admin'); setInviteDefaultReviewer('');
      await fetchData();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(detail || '招待に失敗しました');
    } finally {
      setInviting(false);
    }
  };

  const handleChangeReviewer = async (userId: string) => {
    try {
      await api.patch(`/admin/users/${userId}`, { default_reviewer_id: editReviewerId || null });
      setEditingUserId(null);
      setEditReviewerId('');
      await fetchData();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(detail || '変更に失敗しました');
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">読み込み中...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900">コーチ管理</h1>
      <p className="mt-1 text-sm text-gray-500">ユーザーの追加・編集</p>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-gray-700">ユーザー一覧</h2>
          <button type="button" onClick={() => setShowInvite(true)} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">+ ユーザー追加</button>
        </div>

        {showInvite && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h3 className="text-sm font-medium text-blue-900">新規ユーザー追加</h3>
            <div className="mt-2 space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">メールアドレス</label>
                <input type="email" placeholder="example@sugoi-kaigi.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">名前</label>
                <input type="text" placeholder="名前" value={inviteName} onChange={e => setInviteName(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">ロール</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'coach' | 'feedbacker' | 'admin')}
                  className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="coach">コーチ</option>
                  <option value="feedbacker">フィードバッカー</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
              {(inviteRole === 'coach' || inviteRole === 'feedbacker') && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">デフォルトフィードバッカー</label>
                  <select value={inviteDefaultReviewer} onChange={e => setInviteDefaultReviewer(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm">
                    <option value="">選択してください</option>
                    {feedbackers.map(fb => <option key={fb.id} value={fb.id}>{fb.name}</option>)}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
                <button type="button" onClick={handleInvite} disabled={inviting} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
                  {inviting ? '追加中...' : '追加する'}
                </button>
                <button type="button" onClick={() => setShowInvite(false)} className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600">キャンセル</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 overflow-hidden rounded-lg bg-white shadow-sm">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500">名前</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">ロール</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">ステータス</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">デフォルトフィードバッカー</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </td>
                  <td className="px-4 py-3"><RoleBadge user={user} /></td>
                  <td className="px-4 py-3">
                    {user.cognito_sub ? <span className="text-xs text-green-600">認証済み</span> : <span className="text-xs text-orange-500">招待中</span>}
                  </td>
                  <td className="px-4 py-3">
                    {editingUserId === user.id ? (
                      <div className="flex items-center gap-2">
                        <select value={editReviewerId} onChange={e => setEditReviewerId(e.target.value)} className="rounded border border-gray-300 px-2 py-1 text-sm">
                          <option value="">なし</option>
                          {feedbackers.filter(fb => fb.id !== user.id).map(fb => <option key={fb.id} value={fb.id}>{fb.name}</option>)}
                        </select>
                        <button type="button" onClick={() => handleChangeReviewer(user.id)} className="rounded bg-blue-600 px-2 py-1 text-xs text-white">保存</button>
                        <button type="button" onClick={() => setEditingUserId(null)} className="text-xs text-gray-500">取消</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { setEditingUserId(user.id); setEditReviewerId(''); }}
                        className="text-sm text-gray-600 hover:text-blue-600">
                        {user.default_reviewer_name ?? '—'}
                        <span className="ml-1 text-xs text-blue-500">変更</span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">ユーザーがいません</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
